import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Instagram } from "lucide-react";
import { api } from "@/lib/api";

const TOUR_HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_metamorph-saga/artifacts/oivt9a3p__L2A0180.jpg";

// "The Hill" — same embed used on `/shows/:city`.
const YOUTUBE_VIDEO_ID = "xm-4MQdbrT4";

/**
 * `/tour` — the all-cities discovery page.
 *
 * Visually identical to `/shows/:city` (hero image with title overlay,
 * left-aligned copy, tour-dates stack) but shows every on-sale city
 * together rather than one at a time. Sits between `/store` (the new
 * prominent "About Metamorph live" button under the LIVE heading) and
 * `/tickets` (the lean buy grid).
 */
export default function Tour() {
  const [shows, setShows] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/shows");
        if (alive) setShows(data.shows || []);
      } catch {
        if (alive) setShows([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onSale = (shows || []).filter((s) => s.status === "on_sale" && !s.private);
  const comingSoon = (shows || []).filter((s) => s.status === "coming_soon" && !s.private);

  return (
    <main className="min-h-screen bg-[var(--mm-bg,#050a1a)] text-white" data-testid="tour-page">
      {/* Hero — image-as-background with title overlay, mirrors /shows/:city */}
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] max-h-[70vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${TOUR_HERO_IMAGE})` }}
          data-testid="tour-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mm-bg,#050a1a)] via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <h1
            className="deco-text text-5xl sm:text-7xl uppercase leading-[0.95] mb-1"
            style={{ letterSpacing: "0.1em" }}
            data-testid="tour-title"
          >
            Metamorph
          </h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/55 mb-3">
            By George Maple
          </p>
          <h2
            className="text-2xl sm:text-3xl mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
            data-testid="tour-subtitle"
          >
            Live · July 2026
          </h2>
          <Link
            to="/tickets"
            className="btn-line inline-flex text-sm py-2.5"
            data-testid="tour-cta-tickets-hero"
          >
            Get tickets <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        {/* About the show — same copy and structure as /shows/:city */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">About</p>
        <div
          className="text-[15px] leading-[1.85] text-white/85 space-y-5 mb-12"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          data-testid="tour-bio"
        >
          <p>
            George Maple is an Australian-born, multidisciplinary artist who moves fluidly
            across genres and mediums &mdash; film, music and live performance. Her work
            lives somewhere between the subterranean depths of the human inner world and the
            esoteric, unseen realms of the metaphysical, with threads of fantasy woven
            through. Her voice captivates, drawing you in with a rare ability to float
            effortlessly between power and vulnerability.
          </p>
          <p>
            You may know George from her own work, namely &lsquo;Talk Talk&rsquo;, later
            reworked by DJ Snake into &lsquo;Talk&rsquo;. You may also know her prolific
            catalogue of work for other artists &mdash; a true songwriter at heart, she has
            lent her writing and production to artists including Melanie C, Anna Lunoe,
            Flume, Hayden James, What So Not, Tkay Maidza, Flight Facilities, Jessica
            Mauboy and DJ Snake, quietly amassing over 1 billion streams and multiple award
            nominations along the way.
          </p>
          <p>
            As a performer, George has taken the stage all over the world &mdash; from
            Carmen in Paris and The Enmore in Sydney to guest appearances at Coachella,
            Lollapalooza and EDC. She has built private conceptual performances for Cartier
            and Dom P&eacute;rignon, and earned recognition for her filmmaking, including
            the ACS National Gold Award. In 2020, she screened a self-written and directed
            24-minute live-performance film at the Sydney Opera House.
          </p>
          <p>
            After time away from music to focus on filmmaking, George
            returns to the stage to celebrate the release of her new piece of music,
            &lsquo;<em style={{ color: "#f3e0a6", fontStyle: "italic" }}>Metamorph</em>&rsquo;
            &mdash; performed alongside reworked moments from her discography, in
            hand-picked venues across the globe.
          </p>
          <p>Each performance George creates is unique, built specifically for its environment.</p>
          <p
            className="italic border-l-2 pl-5"
            style={{ borderColor: "rgba(243,224,166,0.45)", color: "#f3e0a6" }}
          >
            In her words &mdash; &ldquo;Metamorph felt like the perfect offering to share
            with the world. It&rsquo;s a small slice of the inner realm of my own psyche. I
            wanted these performances to be luscious and deeply connected &mdash; each one
            will feel like we&rsquo;re all in a moody living room together.&rdquo;
          </p>
          <p>
            <em style={{ color: "#f3e0a6", fontStyle: "italic" }}>Metamorph</em> will be
            available exclusively on 12&Prime; Vinyl and direct digital download for the
            month of July.
          </p>
        </div>

        {/* Tour dates — beneath the blurb so visitors read the story first */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4">Tour dates</p>
        {shows && shows.some((s) => s.rescheduled) && (
          <p className="text-[11px] italic text-white/45 leading-relaxed mb-4" data-testid="rsv-disclaimer">
            ** GM has come down with RSV, a respiratory virus &mdash; new dates have been rescheduled and tickets issued.
          </p>
        )}
        {shows === null ? (
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 mb-12">Loading…</p>
        ) : (
          <div className="flex flex-col gap-3 mb-12 sm:mb-14" data-testid="tour-dates-grid">
            {onSale.map((s) => (
              <Link
                key={s.city}
                to={`/shows/${s.city}`}
                className="border border-white/15 hover:border-[var(--mm-accent)] transition px-5 py-5 group flex items-start justify-between gap-4"
                data-testid={`tour-date-${s.city}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1.5">{s.label}</p>
                  <h3
                    className="text-xl mb-1.5"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
                  >
                    {s.rescheduled && (
                      <span className="font-mono text-[9.5px] uppercase tracking-[0.32em] align-middle text-[#f3e0a6] border border-[#f3e0a6]/40 px-1.5 py-0.5 mr-2">** New date</span>
                    )}
                    {s.date || "Date TBA"}
                  </h3>
                  {s.venue && (
                    <p className="flex items-center gap-1.5 text-[12px] text-white/60">
                      <MapPin className="w-3 h-3" strokeWidth={1.4} /> {s.venue}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span
                    className="tabular-nums"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", fontSize: "20px", fontWeight: 500 }}
                  >
                    {s.currency_symbol}{Math.round(s.price)}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/65 group-hover:text-[var(--mm-accent)] transition inline-flex items-center gap-1">
                    Know more <ArrowRight className="w-3 h-3" strokeWidth={1.4} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Coming soon — register interest section */}
        {comingSoon.length > 0 && (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-4 mt-10">Coming soon</p>
            <div className="flex flex-col gap-3 mb-12 sm:mb-14" data-testid="tour-coming-soon-grid">
              {comingSoon.map((s) => (
                <a
                  key={s.city}
                  href={`mailto:info@theprocess.studio?subject=${encodeURIComponent(`Register interest — ${s.label}`)}&body=${encodeURIComponent(`Hi — I'd like to register my interest for the ${s.label} show. Thank you x`)}`}
                  className="border border-white/15 hover:border-[var(--mm-accent)] transition px-5 py-5 group flex items-start justify-between gap-4"
                  data-testid={`tour-coming-soon-${s.city}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/50 mb-1.5">{s.label}</p>
                    <h3
                      className="text-xl mb-1.5"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "#f3e0a6", letterSpacing: "0.02em", fontWeight: 500 }}
                    >
                      {s.date || "Date TBA"}
                    </h3>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/65 group-hover:text-[var(--mm-accent)] transition inline-flex items-center gap-1">
                      Register interest <ArrowRight className="w-3 h-3" strokeWidth={1.4} />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}

        {/* Direct — shifts the framing of this embed from "music
            release" to filmmaking. The Hill is a music video the artist
            directed, so it lives naturally here as directorial work. */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-2">Direct</p>
        <p
          className="text-[15px] text-white/75 mb-5"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
        >
          Focused on filmmaking.
        </p>
        <div
          className="border border-white/15 mb-6 overflow-hidden"
          style={{ aspectRatio: "16 / 9" }}
        >
          <iframe
            title="George Maple — watch"
            src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1&color=white`}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
            style={{ display: "block", border: 0 }}
            data-testid="tour-youtube"
          />
        </div>
        <p className="text-center mb-8">
          <a
            href="https://www.youtube.com/@GeorgeMapleMusic"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60 hover:text-[var(--mm-accent)]"
          >
            More on YouTube &rarr;
          </a>
        </p>

        {/* Primary CTA */}
        <Link
          to="/tickets"
          className="btn-line w-full justify-center text-sm py-3.5 mb-6"
          data-testid="tour-to-tickets"
        >
          Get tickets <ArrowRight className="w-4 h-4" strokeWidth={1.4} />
        </Link>

        {/* Followups — manifesto + store */}
        <div className="flex flex-col sm:flex-row gap-3 mb-12" data-testid="tour-followups">
          <Link
            to="/manifesto"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Read</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              The manifesto &rarr;
            </span>
          </Link>
          <Link
            to="/store"
            className="flex-1 border border-white/20 hover:border-[var(--mm-accent)] px-5 py-3.5 transition text-center"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 block mb-1">Browse</span>
            <span className="text-[15px] text-white/90" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              The store &rarr;
            </span>
          </Link>
        </div>

        {/* Follow */}
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-white/55 mb-3">Follow</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
          <a
            href="https://www.instagram.com/georgemapleofficial/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
            data-testid="tour-instagram"
          >
            <span className="flex items-center gap-3">
              <Instagram className="w-4 h-4" strokeWidth={1.4} />
              <span className="text-[13px] text-white">@georgemapleofficial</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Instagram &rarr;</span>
          </a>
          <Link
            to="/manifesto"
            className="flex items-center justify-between border border-white/20 hover:border-[var(--mm-accent)] px-4 py-3 transition"
          >
            <span className="text-[13px] text-white">Read the manifesto</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">Read &rarr;</span>
          </Link>
        </div>

        {/* Listen CTA — primary path off the discovery page into the
            music itself. Lands on the homepage where the "Now playing —
            Up Here" widget sits above the fold of the footer. */}
        <Link
          to="/store"
          className="block w-full text-center border border-[var(--mm-accent)] hover:bg-[var(--mm-accent)]/10 px-6 py-5 mt-2 mb-12 transition group"
          data-testid="tour-listen-cta"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--mm-accent)] block mb-2">
            Listen
          </span>
          <span
            className="deco-text uppercase text-2xl sm:text-3xl block"
            style={{ color: "#f3e0a6", letterSpacing: "0.08em" }}
          >
            Listen to Metamorph &rarr;
          </span>
        </Link>

        <p className="text-center mt-14 font-mono text-[10px] uppercase tracking-[0.32em] text-white/40">
          <Link to="/tickets" className="hover:text-white/85">View all shows</Link>
          <span className="mx-2 opacity-30">·</span>
          <Link to="/" className="hover:text-white/85">Back to Metamorph</Link>
        </p>
      </div>
    </main>
  );
}
